import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { BaseComponent } from '../../base/base.component';
import { buildSafeLinksPolicySchema } from 'projects/angular-clarity/src/app/modules/sway/models/baseline-schemas/safe-links-policy.schema';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { SafeLinksPolicy } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/safe-links-policy/model';
import { isValidEmail } from 'projects/angular-clarity/src/app/utils/custom-validators';
import { updateBaseline } from 'projects/angular-clarity/src/app/stores/client/sway/baseline/actions';
import { Baseline } from 'projects/angular-clarity/src/app/stores/client/sway/baseline/model';
import { createExoSafeLinksPolicySuccess } from 'projects/angular-clarity/src/app/stores/client/powershell/exo/safe-links-policy/actions';

@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit, OnChanges {
    policy: SafeLinksPolicy;

    users$: Observable<Array<{ id: string; name: string }>>;
    groups$: Observable<Array<{ id: string; name: string }>>;
    domains$: Observable<Array<{ id: string; name: string }>>;

    sentToErrors: string[] = [];
    exceptIfSentToErrors: string[] = [];

    constructor(
        private fb: FormBuilder,
        private store: Store<any>,
        private action: Actions,
    ) {
        super();
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.policy = this.data?.[0];
    }

    ngOnInit(): void {
        this.policy = this.data?.[0];
        this.observeCreationOfPolicy();
        const schema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = this.initForm(schema);

        this.applyCustomNotificationValidator();
        this.form.get('useCustomNotification')?.valueChanges.subscribe(() => this.applyCustomNotificationValidator());

        this.form.get('ScanUrls')?.valueChanges.subscribe(() => {
            this.form.get('DeliverMessageAfterScan')?.setValue(false);
        });

        const hasErrors = this.form.invalid;
        this.baselineErrorChange.next({ remediate: hasErrors, save: hasErrors });

        this.users$ = this.store
            .select(client(this.tenant_id).graph.users.withMailbox)
            .pipe(map((users) => users.map((u) => ({ id: u['id'], name: u['mail'] }))));

        this.groups$ = this.store
            .select(client(this.tenant_id).graph.groups.all)
            .pipe(
                map((res) =>
                    res.filter((g) => g.mailEnabled).map((g) => ({ id: g.id, name: g.displayName, mail: g.mail })),
                ),
            );

        this.domains$ = this.store
            .select(client(this.tenant_id).graph.domains.verified)
            .pipe(map((domains) => domains.map((d) => ({ id: d.id, name: d.id }))));

        this.observeFormChanges();

        this.applyDefaultIncludeTarget();
    }

    /**
     * Ensures the policy has at least one Include target so the baseline can be
     * saved and remediated.
     *
     * The templated baseline seeds every recipient condition as `null`, but the
     * form requires (and Exchange's `New-SafeLinksRule` mandates) at least one
     * condition — otherwise the form stays invalid and both the Save and
     * Remediate buttons are permanently disabled (OS-433). When no target is
     * configured we default `RecipientDomainIs` to the tenant's verified domains
     * so the policy applies organization-wide. The value is written into the
     * schema on Save/Remediate, keeping the created rule and the deviation check
     * consistent.
     */
    private applyDefaultIncludeTarget(): void {
        const value = this.formValue;
        const hasIncludeTarget =
            (value.SentTo?.length ?? 0) > 0 ||
            (value.SentToMemberOf?.length ?? 0) > 0 ||
            (value.RecipientDomainIs?.length ?? 0) > 0;

        if (hasIncludeTarget) {
            return;
        }

        const sub = this.domains$
            .pipe(
                filter((domains) => domains.length > 0),
                take(1),
            )
            .subscribe((domains) => {
                this.form.get('RecipientDomainIs').patchValue(
                    domains.map((d) => d.name),
                    { emitEvent: true },
                );
            });

        this.subs.push(sub);
    }

    observeCreationOfPolicy() {
        const sub = this.action.pipe(ofType(createExoSafeLinksPolicySuccess)).subscribe((res) => {
            if (res) {
                const policyId = res.data.Identity;
                const data: Baseline = { ...this.baseline, key: policyId };
                const _tenant = this.tenant_id;
                this.store.dispatch(updateBaseline({ _tenant, data }));
            }
        });
        this.subs.push(sub);
    }

    initForm(schema: any): FormGroup {
        const useCustomNotification = !!schema?.contains?.properties?.CustomNotificationText?.const;

        return this.fb.group(
            {
                Identity: [schema?.contains?.properties?.Identity?.const, Validators.required],
                Priority: [
                    schema?.contains?.properties?.Priority?.const ?? 0,
                    [Validators.required, Validators.min(0)],
                ],
                EnableSafeLinksForEmail: [schema?.contains?.properties?.EnableSafeLinksForEmail?.const],
                EnableForInternalSenders: [schema?.contains?.properties?.EnableForInternalSenders?.const ?? false],
                ScanUrls: [schema?.contains?.properties?.ScanUrls?.const ?? false],
                DeliverMessageAfterScan: [schema?.contains?.properties?.DeliverMessageAfterScan?.const ?? false],
                DoNotRewriteUrls: [schema?.contains?.properties?.DoNotRewriteUrls?.items?.enum ?? []],
                DisableUrlRewrite: [schema?.contains?.properties?.DisableUrlRewrite?.const ?? false],
                EnableSafeLinksForTeams: [schema?.contains?.properties?.EnableSafeLinksForTeams?.const ?? false],
                EnableSafeLinksForOffice: [schema?.contains?.properties?.EnableSafeLinksForOffice?.const ?? false],
                TrackClicks: [schema?.contains?.properties?.TrackClicks?.const ?? false],
                AllowClickThrough: [schema?.contains?.properties?.AllowClickThrough?.const ?? false],
                EnableOrganizationBranding: [schema?.contains?.properties?.EnableOrganizationBranding?.const ?? false],
                useCustomNotification: [useCustomNotification],
                EnableMicrosoftTranslator: [schema?.contains?.properties?.EnableMicrosoftTranslator?.const ?? false],
                CustomNotificationText: [schema?.contains?.properties?.CustomNotificationText?.const ?? ''],
                SentTo: [schema?.contains?.properties?.SentTo?.items?.enum ?? []],
                SentToMemberOf: [schema?.contains?.properties?.SentToMemberOf?.items?.enum ?? []],
                RecipientDomainIs: [schema?.contains?.properties?.RecipientDomainIs?.items?.enum ?? []],
                ExceptIfSentTo: [schema?.contains?.properties?.ExceptIfSentTo?.items?.enum ?? []],
                ExceptIfSentToMemberOf: [schema?.contains?.properties?.ExceptIfSentToMemberOf?.items?.enum ?? []],
                ExceptIfRecipientDomainIs: [schema?.contains?.properties?.ExceptIfRecipientDomainIs?.items?.enum ?? []],
            },
            { validators: [requireOneIncludeTarget, requireCustomNotificationText] },
        );
    }

    observeFormChanges(): void {
        this.form.valueChanges.subscribe(() => {
            const value = this.form.getRawValue();

            this.sentToErrors = (value.SentTo || []).filter((item) => !isValidEmail(item));
            this.exceptIfSentToErrors = (value.ExceptIfSentTo || []).filter((item) => !isValidEmail(item));

            const hasErrors = this.form.invalid;
            this.baselineErrorChange.next({ remediate: hasErrors, save: hasErrors });

            const updatedSchema = buildSafeLinksPolicySchema(value);
            this.baselineChange.next({
                ...this.baseline,
                schema: updatedSchema,
            });
        });
    }

    get formValue() {
        return this.form.getRawValue();
    }

    get includeTargetError(): boolean {
        return this.form?.hasError('requireOneIncludeTarget') && this.form?.touched;
    }

    get excludeError(): boolean {
        return this.exceptIfSentToErrors?.length > 0;
    }

    get settingsError(): boolean {
        return this.form?.get('Identity')?.invalid || this.form?.get('Priority')?.invalid;
    }

    get useCustomNotification(): boolean {
        return this.form?.get('useCustomNotification')?.value === true;
    }

    get enableSafeLinksForEmail(): boolean {
        return this.form?.get('EnableSafeLinksForEmail')?.value === true;
    }

    get scanUrls(): boolean {
        return this.form?.get('ScanUrls')?.value === true;
    }

    get trackClicks(): boolean {
        return this.form?.get('TrackClicks')?.value === true;
    }

    addUrlTag = (term: string): string | null => {
        const value = term?.trim();
        return value ? value : null;
    };

    private applyCustomNotificationValidator(): void {
        const ctrl = this.form.get('CustomNotificationText');
        if (!ctrl) return;
        if (this.useCustomNotification) {
            ctrl.setValidators([Validators.required]);
        } else {
            ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
    }
}

function requireOneIncludeTarget(control: AbstractControl): ValidationErrors | null {
    const sentTo: string[] = control.get('SentTo')?.value ?? [];
    const sentToMemberOf: string[] = control.get('SentToMemberOf')?.value ?? [];
    const recipientDomainIs: string[] = control.get('RecipientDomainIs')?.value ?? [];
    const hasInclude = sentTo.length > 0 || sentToMemberOf.length > 0 || recipientDomainIs.length > 0;
    return hasInclude ? null : { requireOneIncludeTarget: true };
}

function requireCustomNotificationText(control: AbstractControl): ValidationErrors | null {
    const useCustom: boolean = control.get('useCustomNotification')?.value === true;
    const text: string = (control.get('CustomNotificationText')?.value ?? '').trim();
    return useCustom && !text ? { requireCustomNotificationText: true } : null;
}
